import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/features/auth/context/authProvider'
import DynamicForm from '@/shared/components/DynamicForm/DynamicForm'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import SettingsPanel from '../components/SettingsPanel'
import { useAccountFormConfig } from '../forms/useAccountFormConfig'
import { useWriteProfile } from '../hooks/useWriteProfile'

const FORM_ID = 'account-settings-form'

interface AccountSectionProps {
  /** Reports a name edited and not yet saved, which leaving would drop. */
  onUnsavedChange: (hasUnsaved: boolean) => void
}

/**
 * The signed-in owner's own profile as a DynamicForm
 * (`useAccountFormConfig`). The name is kept by Save, as the restaurant's
 * details are; the photo is saved as soon as it is cropped or deleted, as the
 * venue's logo is.
 */
function AccountSection({ onUnsavedChange }: AccountSectionProps) {
  const { t, i18n } = useTranslation()
  const { currentUser } = useAuth()
  const { showSnackbar } = useSnackbar()
  const writeProfile = useWriteProfile()
  const { fields, validationSchema } = useAccountFormConfig()

  const savedName = currentUser?.displayName ?? ''
  const email = currentUser?.email ?? ''
  const accountId = currentUser?.uid ?? ''
  const creationTime = currentUser?.metadata?.creationTime
  const lastSignInTime = currentUser?.metadata?.lastSignInTime
  /*
    Re-made when the saved name changes, which resets the form to it: a Save
    does once Firebase has it. The rest is shown, in the interface's
    language. Keyed on these values, not on `currentUser`: the context gets a
    new copy of the user on every token refresh and on a photo upload, and a
    reset then would throw away a name being typed.
  */
  const defaultValues = useMemo(() => {
    const when = (time: string | undefined, withTime: boolean) =>
      time
        ? new Intl.DateTimeFormat(i18n.language, {
            dateStyle: 'long',
            ...(withTime && { timeStyle: 'short' })
          }).format(new Date(time))
        : ''
    return {
      displayName: savedName,
      email,
      accountId,
      createdAt: when(creationTime, false),
      lastSignInAt: when(lastSignInTime, true)
    }
  }, [
    savedName,
    email,
    accountId,
    creationTime,
    lastSignInTime,
    i18n.language
  ])

  // Bumped to start the form again from what is saved.
  const [formKey, setFormKey] = useState(0)

  const save = async (values: Record<string, unknown>) => {
    const displayName = values.displayName as string
    if (displayName === savedName) {
      // Only spaces were added: nothing to write, but the form is put back to
      // what is saved, or it stays "unsaved" and leaving asks about it.
      setFormKey(key => key + 1)
      showSnackbar(
        t('ALERTS.SETTINGS_SAVE_SUCCESS.TITLE'),
        t('ALERTS.SETTINGS_SAVE_SUCCESS.DESCRIPTION'),
        'success'
      )
      return
    }
    try {
      await writeProfile({ displayName })
      showSnackbar(
        t('ALERTS.SETTINGS_SAVE_SUCCESS.TITLE'),
        t('ALERTS.SETTINGS_SAVE_SUCCESS.DESCRIPTION'),
        'success'
      )
    } catch {
      showSnackbar(
        t('ALERTS.SETTINGS_SAVE_ERROR.TITLE'),
        t('ALERTS.SETTINGS_SAVE_ERROR.DESCRIPTION'),
        'error'
      )
    }
  }

  return (
    <SettingsPanel
      lead={t('SETTINGS.ACCOUNT.LEAD')}
      footer={{ formId: FORM_ID }}
    >
      <DynamicForm
        key={formKey}
        id={FORM_ID}
        fields={fields}
        validationSchema={validationSchema}
        defaultValues={defaultValues}
        onSubmit={save}
        onInvalid={() =>
          showSnackbar(
            t('ALERTS.SETTINGS_INVALID.TITLE'),
            t('ALERTS.SETTINGS_INVALID.DESCRIPTION'),
            'error'
          )
        }
        onIsDirtyChange={onUnsavedChange}
        validationMode="onSubmit"
        superCondensed
        sectioned
      />
    </SettingsPanel>
  )
}

export default AccountSection
